import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../shared/components';
import { examWeighting, sourceMaterials, examStructure, backgroundNote, subjectMatters } from './content/overview';
import './StudyGuide.css';

const topicGuides = [
  {
    to: '/study-guide/reading-and-writing',
    title: 'Reading and Writing',
    description: 'How to read each short passage, plus craft and structure, information and ideas, standard English conventions, and expression of ideas.',
  },
  {
    to: '/study-guide/math',
    title: 'Math',
    description: 'How to work every question type in algebra, advanced math, problem-solving and data analysis, and geometry and trigonometry.',
  },
  {
    to: '/study-guide/test-strategies',
    title: 'Test-Taking Strategies',
    description: "What to do when you're not sure of an answer, and how to structure your studying across the whole test.",
  },
];

const referenceGuides = [
  {
    to: '/study-guide/craft-and-structure-reference',
    title: 'Craft and Structure Reference',
    description: 'Passage structures, contrast/continuation signal words, and the words-in-context and cross-text methods.',
  },
  {
    to: '/study-guide/information-and-ideas-reference',
    title: 'Information and Ideas Reference',
    description: 'Central idea vs. detail, textual and quantitative evidence checklists, and the inference test.',
  },
  {
    to: '/study-guide/grammar-reference',
    title: 'Standard English Conventions Reference',
    description: 'Subject-verb agreement, commas, sentence boundaries, apostrophes, pronoun agreement, modifiers, and parallel structure.',
  },
  {
    to: '/study-guide/expression-of-ideas-reference',
    title: 'Expression of Ideas Reference',
    description: 'Transition word categories and the rhetorical synthesis checklist.',
  },
  {
    to: '/study-guide/algebra-reference',
    title: 'Algebra Reference',
    description: 'Linear equation forms, solving equations and inequalities, systems, and graphing inequalities.',
  },
  {
    to: '/study-guide/advanced-math-reference',
    title: 'Advanced Math Reference',
    description: 'Exponent rules, factoring patterns, the quadratic formula, function transformations, and exponential growth and decay.',
  },
  {
    to: '/study-guide/problem-solving-data-analysis-reference',
    title: 'Problem-Solving and Data Analysis Reference',
    description: 'Ratios and units, percent change, center and spread, correlation, probability, and study design.',
  },
  {
    to: '/study-guide/geometry-trigonometry-reference',
    title: 'Geometry and Trigonometry Reference',
    description: 'Area and volume, angle relationships, right triangles, trig ratios, and circles.',
  },
];

export default function StudyGuide() {
  return (
    <div className="study-guide-page">
      <h1>Study Guide</h1>
      <p className="study-guide-intro">
        This overview summarizes what's actually tested on the digital SAT and how much of the exam each
        section covers. Use it to plan your studying, then dig into the domain guides below — each one walks
        through how to work every type of question, not just what it's called, and the strategies guide covers
        what to do when you're not sure of an answer.
      </p>

      <h2 className="study-guide-group-heading">Domain Study Guides</h2>
      <p className="study-guide-group-subheading">
        Original study content, one guide per SAT section — how each question type works and how to solve it, plus
        a shared guide to test-taking strategy and building a study plan.
      </p>
      <div className="study-guide-links study-guide-links--small study-guide-links--three">
        {topicGuides.map((page) => (
          <Link key={page.to} to={page.to} className="study-guide-link-card">
            <Card className="study-guide-card study-guide-card--topic study-guide-card--small">
              <span className="study-guide-card-badge study-guide-card-badge--topic">Domain Guide</span>
              <h2>{page.title}</h2>
              <p>{page.description}</p>
            </Card>
          </Link>
        ))}
      </div>

      <h2 className="study-guide-group-heading">Reference Sheets</h2>
      <p className="study-guide-group-subheading">
        One quick-lookup reference per domain — formulas, rules, and checklists, not full explanations. For the
        how-to-solve walkthroughs, use the domain guides above.
      </p>
      <div className="study-guide-links study-guide-links--small study-guide-links--four">
        {referenceGuides.map((page) => (
          <Link key={page.to} to={page.to} className="study-guide-link-card">
            <Card className="study-guide-card study-guide-card--source study-guide-card--small">
              <span className="study-guide-card-badge study-guide-card-badge--source">Reference</span>
              <h2>{page.title}</h2>
              <p>{page.description}</p>
            </Card>
          </Link>
        ))}
      </div>

      <section className="study-guide-section">
        <h2>About the exam</h2>
        <p>{backgroundNote}</p>
        <ul>
          {examStructure.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section className="study-guide-section">
        <h2>What the exam draws from</h2>
        <p>{sourceMaterials.general}</p>
        <p><strong>Official practice:</strong> {sourceMaterials.official.join(' • ')}</p>
      </section>

      <section className="study-guide-section">
        <h2>Domain weighting</h2>
        <table className="study-weight-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Weight</th>
              <th># Questions</th>
            </tr>
          </thead>
          <tbody>
            {examWeighting.map((row) => (
              <tr key={row.subject}>
                <td>{row.subject}</td>
                <td>{row.weight}%</td>
                <td>{row.questions}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: '#999999' }}>
          Each full digital SAT includes 98 scored questions, 54 in Reading and Writing and 44 in Math.
        </p>
      </section>

      {subjectMatters.map((subject) => (
        <section className="study-guide-section" key={subject.id}>
          <h2>{subject.title}</h2>
          <p className="study-guide-intro" style={{ marginBottom: 12 }}>{subject.weight}</p>
          {subject.components.map((component) => (
            <div className="study-subject-block" key={component.heading}>
              <p className="study-component-heading">{component.heading}</p>
              <ul className="study-component-list">
                {component.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
